import { useState } from 'react'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { Close } from '@mui/icons-material'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from './atoms/Button'
import { callAxios } from '../hooks/useAxios'
import { ID } from '../types/models'

interface FineEntry {
  status: string
  timestamp: number | null
  max_charge_pwr: number
  max_discharge_pwr: number
  open_circuit_v: number
}

interface CoarseEntry {
  ac_offset: number
  charge_cmd_offset: number
  charge_cmd_gain: number
  discharge_cmd_offset: number
  discharge_cmd_gain: number
  posadc_meas_gain: number
  negadc_meas_gain: number
  failure: boolean
}

interface SelftestData {
  max_observed_initial_power: number
  commissioning_state: number
  commissioning_errcode: number
  quick_test_success_ts: number
  full_test_success_ts: number
  discharge_wh: number
  charge_wh: number
}

interface CalibrationEntry<T> {
  data: T
  inserted_at: string
}

interface CalibrationData {
  fine: CalibrationEntry<{ entries: FineEntry[] }> | null
  coarse: CalibrationEntry<{ entries: CoarseEntry[] }> | null
  selftest: CalibrationEntry<SelftestData> | null
}

// Fine: SoC% = 6 + i*4, i=0..23 (24 entries across 310/0, 310/1, 310/2)
function fineSoc(i: number): number { return 6 + i * 4 }
// Coarse: SoC% = 14 + i*16, i=0..5 (6 entries in 311/0)
const COARSE_SOC = [14, 30, 46, 62, 78, 94]

// Port 310 fine-entry powers are stored raw, in the unit the device was
// configured with. The flag is decided in exactly one place — the FCR server's
// `@use_16w_limit` in fcr/lib/fcr/devices/devices.ex: `nominal_power > 30_000`
// (strictly greater; a 30 kW Solis S6 runs 1 W units). Keep this in sync.
export const USE_16W_LIMIT = 30000
export function powerUnit(nominalPower: number): number {
  return nominalPower > USE_16W_LIMIT ? 16 : 1
}

function formatTimestamp(ts: number | null): string {
  if (!ts) return ''
  const d = new Date(ts * 1000)
  return d.toLocaleString('fi-FI', { timeZone: 'Europe/Helsinki' })
}

function formatGain(raw: number): string {
  // Gains are stored as unsigned 16-bit with 0x8000 = 1.0
  return (raw / 32768).toFixed(4)
}

interface PowerBarProps {
  value: number
  max: number
  color: string
}

function PowerBar({ value, max, color }: PowerBarProps) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 16,
            borderRadius: 1,
            backgroundColor: 'rgba(128,128,128,0.2)',
            '& .MuiLinearProgress-bar': { backgroundColor: color }
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 65, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem' }}>
        {value} W
      </Typography>
    </Box>
  )
}

const cellSx = { px: 1, py: 0.5, fontSize: '0.8rem', fontFamily: 'monospace' }
const headerSx = { ...cellSx, fontWeight: 700 }

export interface CalibrationDialogProps {
  open: boolean
  onClose: () => void
  portId?: ID
  nominalPower: number
  hwId: string
  userEmail: string
}

function commissioningStateColor(state: number): string {
  if (state === 0) return '#f44336'
  if (state >= 1 && state <= 98) return '#ffa726'
  if (state === 99) return '#1565c0'
  return '#26a69a'
}

function commissioningErrcodeColor(errcode: number): string {
  if (errcode >= 100) return '#ffa726'
  return '#f44336'
}

export function CalibrationDialog({ open, onClose, portId, nominalPower, hwId, userEmail }: CalibrationDialogProps) {
  const { t } = useTranslation()
  const [data, setData] = useState<CalibrationData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [rerunOpen, setRerunOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [rerunning, setRerunning] = useState(false)

  const pUnit = powerUnit(nominalPower)

  const fetchData = async () => {
    setLoading(true)
    setError(false)
    const url = portId ? `/calibrations/${portId}` : `/admin/calibrations/${hwId}`
    const res = await callAxios<CalibrationData>({ url })
    if (res.success) {
      setData(res.data)
    } else {
      setError(true)
    }
    setLoading(false)
  }

  // Fetch on open
  const handleEnter = () => {
    fetchData()
  }

  const fineEntries = data?.fine?.data?.entries || []
  const coarseEntries = data?.coarse?.data?.entries || []
  const selftest = data?.selftest?.data || null

  // Build coarse lookup by SoC for merged display
  const coarseBySoc: Record<number, CoarseEntry> = {}
  coarseEntries.forEach((entry, i) => {
    if (i < COARSE_SOC.length) {
      coarseBySoc[COARSE_SOC[i]] = entry
    }
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        FCR-kalibraatio {hwId} {userEmail}
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Typography color="error" sx={{ mb: 2 }}><Trans>Failed to load calibration data</Trans></Typography>}

        {!loading && selftest && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: commissioningStateColor(selftest.commissioning_state), fontWeight: 700, fontSize: '0.9rem' }}>
              {selftest.commissioning_state} — {t(`Commissioning-State-${selftest.commissioning_state}`)}
            </Typography>
            {selftest.commissioning_errcode > 0 && (
              <Typography sx={{ color: commissioningErrcodeColor(selftest.commissioning_errcode), fontWeight: 700, fontSize: '0.9rem' }}>
                {selftest.commissioning_errcode} — {t(`Commissioning-Error-${selftest.commissioning_errcode}`)}
              </Typography>
            )}
          </Box>
        )}

        {!loading && data && (
          <>
            {/* Fine + Coarse merged table */}
            {fineEntries.length > 0 && (
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerSx}>SoC%</TableCell>
                      <TableCell sx={headerSx}><Trans>Date calibrated</Trans></TableCell>
                      <TableCell sx={headerSx}><Trans>Charge power</Trans></TableCell>
                      <TableCell sx={headerSx}><Trans>Discharge power</Trans></TableCell>
                      <TableCell sx={headerSx}>OCV</TableCell>
                      <TableCell sx={headerSx}>AC offs</TableCell>
                      <TableCell sx={headerSx}>Cha offs</TableCell>
                      <TableCell sx={headerSx}>Cha gain</TableCell>
                      <TableCell sx={headerSx}>Dsch offs</TableCell>
                      <TableCell sx={headerSx}>Dsch gain</TableCell>
                      <TableCell sx={headerSx}>pos_gain</TableCell>
                      <TableCell sx={headerSx}>neg_gain</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fineEntries.map((entry, i) => {
                      const soc = fineSoc(i)
                      const coarse = coarseBySoc[soc]
                      const isInvalid = entry.status === 'INVALID'
                      const isPrepared = entry.status === 'PREPARED_AVG'
                      const rowColor = isInvalid ? 'error.dark' : isPrepared ? 'warning.dark' : undefined
                      const chargePwr = entry.max_charge_pwr * pUnit
                      const dischargePwr = entry.max_discharge_pwr * pUnit

                      return (
                        <TableRow key={i} sx={{ backgroundColor: rowColor }}>
                          <TableCell sx={cellSx}>{soc}%</TableCell>
                          <TableCell sx={cellSx}>
                            {isInvalid ? 'INVALID' : isPrepared ? 'PREPARED_AVG' : formatTimestamp(entry.timestamp)}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <PowerBar value={chargePwr} max={nominalPower || 20000} color="#4a7abf" />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <PowerBar value={dischargePwr} max={nominalPower || 20000} color="#c9b035" />
                          </TableCell>
                          <TableCell sx={cellSx}>{(entry.open_circuit_v / 16).toFixed(1)}</TableCell>
                          <TableCell sx={cellSx}>{coarse ? `${coarse.ac_offset > 0 ? '+' : ''}${coarse.ac_offset}` : ''}</TableCell>
                          <TableCell sx={cellSx}>{coarse ? coarse.charge_cmd_offset : ''}</TableCell>
                          <TableCell sx={cellSx}>{coarse ? formatGain(coarse.charge_cmd_gain) : ''}</TableCell>
                          <TableCell sx={cellSx}>{coarse ? coarse.discharge_cmd_offset : ''}</TableCell>
                          <TableCell sx={cellSx}>{coarse ? formatGain(coarse.discharge_cmd_gain) : ''}</TableCell>
                          <TableCell sx={cellSx}>{coarse ? coarse.posadc_meas_gain : ''}</TableCell>
                          <TableCell sx={cellSx}>{coarse ? coarse.negadc_meas_gain : ''}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Selftest summary */}
            {selftest && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {selftest.quick_test_success_ts > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Quick test</Typography>
                    {formatTimestamp(selftest.quick_test_success_ts)}
                  </Box>
                )}
                {selftest.full_test_success_ts > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Full test</Typography>
                    {formatTimestamp(selftest.full_test_success_ts)}
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Max Observed Initial P</Typography>
                  {selftest.max_observed_initial_power} W
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>CharCap</Typography>
                  {selftest.charge_wh} Wh
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>DschCap</Typography>
                  {selftest.discharge_wh} Wh
                </Box>
              </Box>
            )}

            {/* No data state */}
            {!data.fine && !data.coarse && !data.selftest && (
              <Typography><Trans>No calibration data available</Trans></Typography>
            )}
          </>
        )}

        {/* Rerun calibration */}
        {!loading && data && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              sx={{ backgroundColor: 'warning.dark' }}
              onClick={() => { setRerunOpen(true); setConfirmText('') }}
              data-cy="button-Rerun Calibration"
            >
              <Trans>Rerun calibration</Trans>
            </Button>
          </Box>
        )}

        {/* Rerun confirmation dialog */}
        <Dialog open={rerunOpen} onClose={() => setRerunOpen(false)}>
          <DialogTitle sx={{ backgroundColor: 'error.dark', color: 'error.contrastText' }}>
            <Trans>Rerun calibration</Trans>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography sx={{ mb: 2, fontWeight: 700, color: 'error.main' }} data-cy="text-Rerun warning">
              {/* A commissioned device may be in the reserve; the rerun takes it out (FCR pauses its bidding and
                  deletes its constraints), and it comes back only through approval. */}
              {selftest?.commissioning_state === 100
                ? t('Are you sure? Starting a new calibration removes the equipment from the reserve. It returns to the reserve only after the new calibration has been approved.')
                : t('Are you 100% sure? If the device is already on reserve markets, doing this will remove it.')}
            </Typography>
            <Typography sx={{ mb: 2 }}>
              <Trans>Type the device ID to confirm</Trans>: <b>{hwId}</b>
            </Typography>
            <TextField
              fullWidth
              size="small"
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder={hwId}
              data-cy="text-Confirm HW ID"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRerunOpen(false)}><Trans>Cancel</Trans></Button>
            <Button
              sx={{ backgroundColor: 'error.dark' }}
              disabled={confirmText !== hwId || rerunning}
              onClick={async () => {
                setRerunning(true)
                const rerunUrl = portId ? `/calibrations/${portId}/rerun` : `/admin/calibrations/${hwId}/rerun`
                await callAxios({ method: 'POST', url: rerunUrl })
                setRerunning(false)
                setRerunOpen(false)
                onClose()
              }}
              data-cy="button-Confirm Rerun"
            >
              {rerunning ? '...' : <Trans>Yes, rerun calibration</Trans>}
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
    </Dialog>
  )
}
